import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { errMsg } from "../lib/apiErrors";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { AuthImg } from "../components/AuthImg";
import { ServiceLamp } from "../components/ServiceLamp";
import { toast } from "sonner";
import { Plus, Car as CarIcon, Search, Download, FileText, AlertCircle, BarChart3, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { exportCSV } from "../lib/csvExport";
import { exportBrandedPDF } from "../lib/pdfExport";
import { SparePartsBanner } from "../components/SparePartsBanner";
import VehicleTabsNav from "../components/VehicleTabsNav";

const COLOR_MAP = {
  alb: "#FFFFFF", weiss: "#FFFFFF", white: "#FFFFFF",
  negru: "#000000", schwarz: "#000000", black: "#000000",
  gri: "#9CA3AF", grau: "#9CA3AF", grey: "#9CA3AF", gray: "#9CA3AF",
  argintiu: "#C0C0C0", silber: "#C0C0C0", silver: "#C0C0C0",
  rosu: "#DC2626", "roșu": "#DC2626", rot: "#DC2626", red: "#DC2626",
  albastru: "#2563EB", blau: "#2563EB", blue: "#2563EB",
  verde: "#16A34A", grun: "#16A34A", "grün": "#16A34A", green: "#16A34A",
  galben: "#FBBF24", gelb: "#FBBF24", yellow: "#FBBF24",
  portocaliu: "#F97316", orange: "#F97316",
  maro: "#92400E", braun: "#92400E", brown: "#92400E",
  bej: "#D6A77A", beige: "#D6A77A",
  bordeaux: "#7F1D1D", "vișiniu": "#7F1D1D",
};
function cssColorFor(name) {
  if (!name) return "#9CA3AF";
  const k = String(name).trim().toLowerCase();
  if (COLOR_MAP[k]) return COLOR_MAP[k];
  // Potrivire parțială: "schwarz met", "weiss perleffekt", "gri metalizat" etc.
  const keys = Object.keys(COLOR_MAP).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (k.includes(key)) return COLOR_MAP[key];
  }
  // try direct CSS color (#hex or named)
  return name;
}

// Tip vehicul: explicit (vehicle_type) sau euristic după model/locuri
const VAN_RE = /transit|master|jumpy|crafter|sprinter|ducato|boxer|vito|transporter|expert|scudo|trafic/i;
export function carVehicleType(c) {
  if (c?.vehicle_type) return c.vehicle_type;
  if ((c?.nr_locuri || 0) >= 8) return "bus";
  if (VAN_RE.test(`${c?.marca || ""} ${c?.model || ""}`)) return "duba";
  return "normal";
}
export const VEHICLE_TYPES = [
  { code: "duba",   label: "Dubă",              emoji: "🚐", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { code: "normal", label: "Autovehicul",        emoji: "🚗", cls: "bg-zinc-100 text-zinc-700 border-zinc-300" },
  { code: "bus",    label: "Bus (multe locuri)", emoji: "🚌", cls: "bg-purple-100 text-purple-800 border-purple-300" },
];

// Lista câmpurilor esențiale care, dacă lipsesc, trebuie semnalate
const ESSENTIAL_CAR_FIELDS = [
  { key: "numar_inmatriculare", label: "Nr. înmatriculare" },
  { key: "marca", label: "Marcă" },
  { key: "model", label: "Model" },
  { key: "chassis_nr", label: "Chassis Nr (VIN)" },
  { key: "stamm_nr", label: "Stamm Nr" },
  { key: "an_fabricatie", label: "An fabricație" },
  { key: "culoare", label: "Culoare" },
  { key: "kilometri", label: "Kilometri" },
];

export function missingCarFields(car) {
  if (!car) return [];
  return ESSENTIAL_CAR_FIELDS.filter((f) => {
    const v = car[f.key];
    if (f.key === "kilometri") return !v || Number(v) <= 0;
    if (f.key === "an_fabricatie") return !v || Number(v) <= 0;
    return !v || String(v).trim() === "";
  });
}

// Documente obligatorii pe autoturism: Talon + Poliță asigurare
// (Contract vânzare-cumpărare e opțional)
export function missingCarDocs(car) {
  const docs = (car && car.documente) || [];
  const has = (regex) => docs.some((d) => d?.doc_type && regex.test(d.doc_type));
  const missing = [];
  if (!has(/talon/i)) missing.push({ key: "talon", label: "Talon" });
  if (!has(/asigurare|polit/i)) missing.push({ key: "polita_asigurare", label: "Poliță asigurare" });
  return missing;
}

export default function Autoturisme() {
  const { canDo, user } = useAuth();
  const navigate = useNavigate();
  const isMechanic = user?.role === "magazioner";
  const [rows, setRows] = useState([]);
  const [partsCounts, setPartsCounts] = useState({}); // {car_id: {count, total_qty, low_stock}}
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ numar_inmatriculare: "", marca: "", model: "" });
  const [persons, setPersons] = useState([]);
  const [quickDriverCar, setQuickDriverCar] = useState(null);
  // OCR talon: starea de extragere și fișierul ales
  const [talonFile, setTalonFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractInfo, setExtractInfo] = useState(null); // { fields_count, ts }
  const [eqOpen, setEqOpen] = useState(false);
  const [eqName, setEqName] = useState("");

  const submitEquipment = async () => {
    try {
      await api.post("/cars", { model: eqName.trim(), is_equipment: true, kilometri: 0 });
      toast.success("Echipament adăugat");
      setEqOpen(false);
      setEqName("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canQuickAssign = user?.role === "admin" || user?.role === "planificator";

  const load = async () => {
    const [fleetRes, partsRes] = await Promise.all([
      api.get("/fleet/overview"),
      api.get("/inventory/parts-counts-by-car").catch(() => ({ data: {} })),
    ]);
    setRows(fleetRes.data);
    setPartsCounts(partsRes.data || {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (canQuickAssign) {
      api.get("/persons").then((r) => setPersons((r.data || []).filter((p) => p.active !== false))).catch(() => {});
    }
  }, [canQuickAssign]);

  useEffect(() => {
    if (window.location.hash === "#new" && canDo("AUTOTURISME", "add")) {
      setOpen(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [canDo]);

  const [statusFilter, setStatusFilter] = useState("all");
  const [personalFilter, setPersonalFilter] = useState("all"); // all | only-personal | hide-personal
  // Sync filter from URL ?status=defect (used by Magazioner sidebar shortcut)
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const urlStatus = searchParams.get("status");
    if (urlStatus && ["defect", "functional", "all"].includes(urlStatus)) {
      setStatusFilter(urlStatus);
    }
    if (searchParams.get("personal") === "only") setPersonalFilter("only-personal");
  }, [searchParams]);
  const [alertFilter, setAlertFilter] = useState("all");
  const [docsFilter, setDocsFilter] = useState("all"); // all | with | without
  const [typeFilter, setTypeFilter] = useState("all"); // all | duba | normal | bus

  // Notă: NU mai re-sortăm pe frontend — backend-ul folosește `_canonical_cars_sort_key`
  // (severitate descendent → alfabetic nr. înmatriculare) și aceeași ordine se aplică
  // în Excel-ul exportat. O singură regulă pentru toate listele de mașini.
  const filtered = rows.filter((r) => {
    const q = filter.toLowerCase();
    if (q && !`${r.numar_inmatriculare || ""} ${r.marca || ""} ${r.model || ""}`.toLowerCase().includes(q)) return false;
    // Excludem mașinile vândute și echipamentele din lista principală
    if (r.status === "sold") return false;
    if (r.is_equipment) return false;
    if (statusFilter === "defect" && r.status !== "defect") return false;
    if (statusFilter === "functional" && r.status === "defect") return false;
    if (typeFilter !== "all" && carVehicleType(r) !== typeFilter) return false;
    if (alertFilter !== "all") {
      const today = new Date().toISOString().slice(0, 10);
      const mfkExpired = r.mfk_expire && r.mfk_expire <= today;
      const revizieDue = r.revizie_next_km && (r.kilometri || 0) >= r.revizie_next_km;
      if (alertFilter === "mfk" && !mfkExpired) return false;
      if (alertFilter === "revizie" && !revizieDue) return false;
      if (alertFilter === "any-alert" && !(mfkExpired || revizieDue)) return false;
    }
    if (docsFilter === "with" && !(r.documente?.length > 0)) return false;
    if (docsFilter === "without" && r.documente?.length > 0) return false;
    if (personalFilter === "only-personal" && !r.is_personal) return false;
    if (personalFilter === "hide-personal" && r.is_personal) return false;
    return true;
  });

  const personalCars = rows.filter((r) => r.is_personal && r.status !== "sold");

  const soldCars = rows.filter((r) => r.status === "sold" && !r.is_equipment);

  const equipmentItems = rows.filter((r) => r.is_equipment);

  const carsWithoutDocs = rows.filter((r) => r.status !== "sold" && !r.is_equipment && !(r.documente?.length > 0)).length;

  const submit = async () => {
    try {
      await api.post("/cars", form);
      toast.success("Autoturism adăugat");
      setOpen(false);
      setForm({ numar_inmatriculare: "", marca: "", model: "" });
      setTalonFile(null);
      setExtractInfo(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Handler OCR talon — trimite fișierul la backend, completează automat formularul
  const handleExtractTalon = async (selectedFile) => {
    if (!selectedFile) return;
    setExtracting(true);
    setExtractInfo(null);
    const tid = toast.loading("📄 Citesc talonul cu AI...");
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      const res = await api.post("/cars/extract-talon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const x = res.data?.extracted || {};
      // Map câmpuri AI → câmpuri form (păstrăm valorile deja completate manual).
      // Convertim explicit la tipurile așteptate de Input (string-uri text).
      const toStr = (v) => (v == null || v === "" ? "" : String(v));
      const toInt = (v) => {
        if (v == null || v === "") return "";
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? "" : n;
      };
      const mapped = {
        ...form,
        numar_inmatriculare: form.numar_inmatriculare || toStr(x.numar_inmatriculare),
        marca: form.marca || toStr(x.marca),
        model: form.model || toStr(x.model),
        chassis_nr: form.chassis_nr || toStr(x.chassis_nr),
        stamm_nr: form.stamm_nr || toStr(x.stamm_nr),
        typenschein: form.typenschein || toStr(x.typenschein),
        an_fabricatie: form.an_fabricatie || toInt(x.an_fabricatie),
        culoare: form.culoare || toStr(x.culoare),
        mfk_expire: form.mfk_expire || toStr(x.mfk_expire),
        mfk_last: form.mfk_last || toStr(x.mfk_last),
        // ⚠️ data_achizitie = câmpul 38 de pe talon (Im Verkehr gesetzt am — pe proprietarul curent).
        // NU folosim prima_inmatriculare (câmpul 1) ca fallback — sunt date diferite conceptual.
        data_achizitie: form.data_achizitie || toStr(x.data_achizitie),
        prima_inmatriculare: form.prima_inmatriculare || toStr(x.prima_inmatriculare),
      };
      setForm(mapped);
      const nonNull = Object.entries(x).filter(([, v]) => v != null && v !== "").length;
      setExtractInfo({ fields: nonNull, raw: x });
      toast.success(`Talon citit: ${nonNull} câmpuri completate automat`, { id: tid });
    } catch (e) {
      toast.error(errMsg(e, "Eroare extragere AI"), { id: tid });
    } finally {
      setExtracting(false);
    }
  };

  const notifStatus = (c) => {
    const today = new Date().toISOString().slice(0, 10);
    let red = false, orange = false, yellow = false;
    // MFK expired = roșu strict
    if (c.mfk_expire && c.mfk_expire <= today) red = true;
    // Revizie: gradient pe diferența km de la ultima revizie
    const lastKm = Number(c.revizie_last_km || 0);
    const km = Number(c.kilometri || 0);
    const gap = lastKm > 0 ? km - lastKm : 0;
    if (gap > 20000) red = true;
    else if (gap > 15000) orange = true;
    else if (gap > 10000) yellow = true;
    // Revizie scadentă după pragul explicit (revizie_next_km) → minim portocaliu
    if (c.revizie_next_km && km >= Number(c.revizie_next_km) && !red) orange = true;
    return { red, orange, yellow };
  };

  const isDefect = (c) => c.status === "defect";

  return (
    <div data-testid="page-autoturisme">
      <VehicleTabsNav />
      <SparePartsBanner />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Autoturisme</h1>
          <p className="text-sm text-zinc-500 mt-1">Flotă auto, MFK, revizie, DPF, combustibil</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="open-fuel-report-btn">
            <Link to="/rapoarte/combustibil">
              <BarChart3 size={16} className="mr-2"/> Rapoarte
            </Link>
          </Button>
          <Button
            variant="outline"
            data-testid="export-cars-csv"
            onClick={() => exportCSV(filtered, [
              { key: "numar_inmatriculare", label: "Număr" },
              { key: "marca", label: "Marcă" },
              { key: "model", label: "Model" },
              { key: "kilometri", label: "Kilometri" },
              { key: "mfk_expire", label: "MFK expiră" },
              { key: "revizie_next_km", label: "Revizie km" },
              { key: (r) => (r.status === "defect" ? "Defect" : "Funcțional"), label: "Status" },
            ], "autoturisme")}
          >
            <Download size={16} className="mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            data-testid="export-cars-pdf"
            onClick={async () => {
              try {
                await exportBrandedPDF({
                  title: "Raport flotă autoturisme",
                  subtitle: `${filtered.length} ${filtered.length === 1 ? "autoturism" : "autoturisme"}${filtered.length !== rows.length ? ` (filtrat din ${rows.length})` : ""}`,
                  sections: [{
                    table: {
                      columns: ["Număr", "Marcă / Model", "Km", "MFK", "Revizie km", "Status"],
                      rows: filtered.map((c) => [
                        c.numar_inmatriculare || "—",
                        `${c.marca || ""} ${c.model || ""}`.trim(),
                        c.kilometri ? String(c.kilometri) : "—",
                        c.mfk_expire || "—",
                        c.revizie_next_km ? String(c.revizie_next_km) : "—",
                        c.status === "defect" ? "DEFECT" : "Funcțional",
                      ]),
                    },
                  }],
                  filename: `autoturisme-${new Date().toISOString().slice(0, 10)}.pdf`,
                });
                toast.success("PDF generat");
              } catch { toast.error("Eroare PDF"); }
            }}
          >
            <FileText size={16} className="mr-2"/> Export PDF
          </Button>
          <Button
            variant="outline"
            data-testid="download-taloane-zip"
            title="Descarcă într-un ZIP toate taloanele mașinilor active, nevândute"
            onClick={async () => {
              const tid = toast.loading("Se pregătește arhiva cu taloane...");
              try {
                const res = await api.get("/cars/taloane.zip", { responseType: "blob" });
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                const today = new Date().toISOString().slice(0, 10);
                a.download = `taloane-active-${today}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                const filesCount = res.headers?.["x-talon-files"];
                const missingCount = res.headers?.["x-talon-missing"];
                let msg = "Arhivă descărcată";
                if (filesCount) msg += ` · ${filesCount} taloane`;
                if (missingCount && parseInt(missingCount, 10) > 0) msg += ` · ${missingCount} mașini fără talon`;
                toast.success(msg, { id: tid });
              } catch (e) {
                const detail = e?.response?.data?.detail || e?.message || "Eroare descărcare";
                toast.error(detail, { id: tid });
              }
            }}
          >
            <Download size={16} className="mr-2"/> Taloane (ZIP)
          </Button>
          <Button
            variant="outline"
            data-testid="export-cars-xlsx"
            title="Exportă lista mașinilor active într-un fișier Excel (Marca, Model, An, Motor, Fahrgestell Nr.)"
            onClick={async () => {
              const tid = toast.loading("Se generează Excel-ul...");
              try {
                const res = await api.get("/cars/export.xlsx", { responseType: "blob" });
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                const today = new Date().toISOString().slice(0, 10);
                a.download = `masini_active_${today}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Lista mașinilor descărcată", { id: tid });
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Eroare la export", { id: tid });
              }
            }}
          >
            <FileSpreadsheet size={16} className="mr-2"/> Exportă lista (Excel)
          </Button>
          {canDo("AUTOTURISME", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setTalonFile(null); setExtractInfo(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-car-btn" className="bg-[var(--primary)] hover:bg-[var(--primary-hover)]">
                <Plus size={16} className="mr-2" /> Adaugă autoturism
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Autoturism nou</DialogTitle>
                <DialogDescription className="text-xs text-zinc-500">
                  Adaugă o mașină nouă. Poți încărca talonul ca să se completeze automat datele.
                </DialogDescription>
              </DialogHeader>

              {/* OCR Talon — upload + auto-extract */}
              <div className="rounded-lg border-2 border-dashed border-cyan-400 bg-cyan-50/50 p-3 mb-3" data-testid="talon-ocr-zone">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">🤖</div>
                  <div className="flex-1">
                    <div className="font-semibold text-cyan-900 text-sm">Citește automat datele din talon</div>
                    <div className="text-[11px] text-cyan-800 mt-0.5">
                      Încarcă o poză sau PDF cu talonul (Fahrzeugausweis). AI-ul va completa formularul de mai jos automat.
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <input
                        type="file"
                        id="talon-file-input"
                        accept="image/*,application/pdf,.pdf,.heic,.heif"
                        className="hidden"
                        data-testid="talon-file-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          setTalonFile(f);
                          handleExtractTalon(f);
                          e.target.value = ""; // reset pentru re-upload
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={extracting}
                        onClick={() => document.getElementById("talon-file-input")?.click()}
                        data-testid="talon-upload-btn"
                        className="bg-white border-cyan-400 text-cyan-900 hover:bg-cyan-100"
                      >
                        {extracting ? "🔄 Se procesează..." : "📷 Selectează talon"}
                      </Button>
                      {talonFile && !extracting && (
                        <span className="text-[11px] text-zinc-600 truncate max-w-[200px]" title={talonFile.name}>
                          📎 {talonFile.name}
                        </span>
                      )}
                      {extractInfo && (
                        <span className="text-[11px] text-emerald-700 font-semibold" data-testid="talon-extract-status">
                          ✓ {extractInfo.fields} câmpuri detectate
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Formular — pre-completat după extragere, editabil */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Număr înmatriculare</Label>
                  <Input
                    data-testid="new-car-plate"
                    value={form.numar_inmatriculare || ""}
                    onChange={(e) => setForm({ ...form, numar_inmatriculare: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Marcă</Label>
                  <Input
                    data-testid="new-car-marca"
                    value={form.marca || ""}
                    onChange={(e) => setForm({ ...form, marca: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input
                    data-testid="new-car-model"
                    value={form.model || ""}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Chassis Nr (VIN)</Label>
                  <Input
                    data-testid="new-car-chassis"
                    value={form.chassis_nr || ""}
                    onChange={(e) => setForm({ ...form, chassis_nr: e.target.value })}
                    placeholder="17 caractere"
                  />
                </div>
                <div>
                  <Label>Stammnummer</Label>
                  <Input
                    data-testid="new-car-stamm"
                    value={form.stamm_nr || ""}
                    onChange={(e) => setForm({ ...form, stamm_nr: e.target.value })}
                    placeholder="ex: 123.456.789"
                  />
                </div>
                <div>
                  <Label>An fabricație <span className="text-zinc-400 text-[10px]">(câmp 36 — Baujahr)</span></Label>
                  <Input
                    type="number"
                    data-testid="new-car-an"
                    value={form.an_fabricatie || ""}
                    onChange={(e) => setForm({ ...form, an_fabricatie: e.target.value ? parseInt(e.target.value, 10) : "" })}
                    placeholder="ex: 2020"
                  />
                </div>
                <div>
                  <Label>Culoare</Label>
                  <Input
                    data-testid="new-car-culoare"
                    value={form.culoare || ""}
                    onChange={(e) => setForm({ ...form, culoare: e.target.value })}
                    placeholder="ex: alb"
                  />
                </div>
                <div>
                  <Label>Typenschein (CH)</Label>
                  <Input
                    data-testid="new-car-typenschein"
                    value={form.typenschein || ""}
                    onChange={(e) => setForm({ ...form, typenschein: e.target.value })}
                    placeholder="ex: 1AB123"
                  />
                </div>
                <div>
                  <Label>Ultimul MFK <span className="text-zinc-400 text-[10px]">(câmp 39 — Letzte Prüfung)</span></Label>
                  <Input
                    type="date"
                    data-testid="new-car-mfk-last"
                    value={form.mfk_last || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      const next = { ...form, mfk_last: v };
                      // Auto-calculează expirarea +2 ani, doar dacă nu e setată manual
                      if (v && !form.mfk_expire) {
                        const d = new Date(v);
                        if (!isNaN(d.getTime())) {
                          const exp = new Date(d.getFullYear() + 2, d.getMonth(), d.getDate());
                          next.mfk_expire = exp.toISOString().slice(0, 10);
                        }
                      }
                      setForm(next);
                    }}
                  />
                </div>
                <div>
                  <Label>MFK expiră <span className="text-zinc-400 text-[10px]">(auto: ultimul MFK + 2 ani)</span></Label>
                  <Input
                    type="date"
                    data-testid="new-car-mfk-expire"
                    value={form.mfk_expire || ""}
                    onChange={(e) => setForm({ ...form, mfk_expire: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Data cumpărării <span className="text-zinc-400 text-[10px]">(câmp 38 — Im Verkehr gesetzt am)</span></Label>
                  <Input
                    type="date"
                    data-testid="new-car-data-achizitie"
                    value={form.data_achizitie || ""}
                    onChange={(e) => setForm({ ...form, data_achizitie: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Prima înmatriculare <span className="text-zinc-400 text-[10px]">(câmp 1 — 1. Inverkehrsetzung)</span></Label>
                  <Input
                    type="date"
                    data-testid="new-car-prima-inmatriculare"
                    value={form.prima_inmatriculare || ""}
                    onChange={(e) => setForm({ ...form, prima_inmatriculare: e.target.value })}
                    placeholder="data primei înmatriculări globale"
                  />
                </div>
              </div>

              {extractInfo?.raw && Object.keys(extractInfo.raw).filter((k) => !["numar_inmatriculare", "marca", "model", "chassis_nr", "stamm_nr", "typenschein", "an_fabricatie", "culoare", "mfk_expire", "mfk_last", "mfk_expire_computed_from_last_check", "data_achizitie", "prima_inmatriculare"].includes(k) && extractInfo.raw[k] != null).length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-zinc-600 hover:text-zinc-900">
                    📋 Alte date extrase din talon (informativ — vor fi salvate la primul edit detaliat)
                  </summary>
                  <div className="mt-1.5 grid grid-cols-2 gap-1 bg-zinc-50 border rounded p-2 text-[11px]" data-testid="talon-extra-fields">
                    {Object.entries(extractInfo.raw)
                      .filter(([k, v]) => v != null && v !== "" && !["numar_inmatriculare", "marca", "model", "chassis_nr", "stamm_nr", "typenschein", "an_fabricatie", "culoare", "mfk_expire", "mfk_last", "mfk_expire_computed_from_last_check", "data_achizitie", "prima_inmatriculare"].includes(k))
                      .map(([k, v]) => (
                        <div key={k} className="flex gap-1">
                          <span className="text-zinc-500 font-mono">{k}:</span>
                          <span className="text-zinc-800 font-semibold truncate">{String(v)}</span>
                        </div>
                      ))}
                  </div>
                </details>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
                <Button data-testid="submit-car" onClick={submit} disabled={!form.numar_inmatriculare?.trim()}>Salvează</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
          {canDo("AUTOTURISME", "add") && (
          <Dialog open={eqOpen} onOpenChange={setEqOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-equipment-btn" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                <Plus size={16} className="mr-2" /> Adaugă echipament
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm" data-testid="add-equipment-dialog">
              <DialogHeader>
                <DialogTitle>🛠 Echipament nou</DialogTitle>
                <DialogDescription className="text-xs text-zinc-500">
                  Canistră, generator etc. — doar evidență combustibil, fără acte, MFK sau șoferi.
                </DialogDescription>
              </DialogHeader>
              <div>
                <Label>Denumire echipament</Label>
                <Input data-testid="new-equipment-name" value={eqName} onChange={(e) => setEqName(e.target.value)} placeholder="ex: Canistră diesel, Generator curent" autoFocus />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEqOpen(false)}>Anulează</Button>
                <Button data-testid="submit-equipment" onClick={submitEquipment} disabled={!eqName.trim()}>Salvează</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <CarConsumablesStockBanner />
      {(() => {
        // Banner global: mașini cu date lipsă pentru revizie (admin only relevance,
        // dar vizibil oricui ca să atragă atenția)
        const REVIZIE_REQUIRED = (c) =>
          !c.is_personal && !c.is_equipment && !c.sold_at && (
            !c.oil_liters_per_service || Number(c.oil_liters_per_service) <= 0 ||
            !c.engine_oil_type ||
            !c.filter_oil_type || !c.filter_oil_dimensions ||
            !c.filter_air_type || !c.filter_air_dimensions ||
            !c.filter_fuel_type || !c.filter_fuel_dimensions ||
            !c.filter_cabin_type || !c.filter_cabin_dimensions
          );
        const incomplete = (rows || []).filter(REVIZIE_REQUIRED);
        if (incomplete.length === 0) return null;
        return (
          <div className="card-flat p-4 mb-4 border-l-4 border-amber-500 bg-amber-50" data-testid="revizie-missing-global-banner">
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none">⚠️</span>
              <div className="flex-1">
                <div className="font-bold text-amber-900">{incomplete.length} {incomplete.length === 1 ? "mașină are" : "mașini au"} date lipsă pentru revizie</div>
                <div className="text-xs text-amber-800 mt-0.5">Lipsesc: ulei motor și/sau filtre (tip + dimensiuni). Completează pentru a putea comanda piesele corecte la timp.</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {incomplete.slice(0, 12).map((c) => (
                    <Link
                      key={c.id}
                      to={`/autoturisme/${c.id}`}
                      className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                    >
                      {c.numar_inmatriculare || c.label || c.id.slice(0, 6)}
                    </Link>
                  ))}
                  {incomplete.length > 12 && <span className="text-[11px] text-amber-700 self-center">+{incomplete.length - 12}</span>}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <div className="card-flat p-4 mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-zinc-400" />
          <Input placeholder="Caută după număr, marcă, model..." value={filter} onChange={(e) => setFilter(e.target.value)} className="border-0 shadow-none focus-visible:ring-0" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]" data-testid="filter-cars-status"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate statusurile</SelectItem>
            <SelectItem value="functional">Funcțional</SelectItem>
            <SelectItem value="defect">Defect</SelectItem>
          </SelectContent>
        </Select>
        <Select value={alertFilter} onValueChange={setAlertFilter}>
          <SelectTrigger className="w-[170px]" data-testid="filter-cars-alert"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Fără filtru alerte</SelectItem>
            <SelectItem value="any-alert">Cu alertă (orice)</SelectItem>
            <SelectItem value="mfk">MFK expirat</SelectItem>
            <SelectItem value="revizie">Revizie scadentă</SelectItem>
          </SelectContent>
        </Select>
        <Select value={docsFilter} onValueChange={setDocsFilter}>
          <SelectTrigger className="w-[180px]" data-testid="filter-cars-docs"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate (cu/fără documente)</SelectItem>
            <SelectItem value="without">📎 Fără documente ({carsWithoutDocs})</SelectItem>
            <SelectItem value="with">📎 Cu documente</SelectItem>
          </SelectContent>
        </Select>
        <Select value={personalFilter} onValueChange={setPersonalFilter}>
          <SelectTrigger className="w-[200px]" data-testid="filter-cars-personal"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate (operațional + personal)</SelectItem>
            <SelectItem value="hide-personal">🚙 Doar operaționale</SelectItem>
            <SelectItem value="only-personal">🚫 Doar personale ({personalCars.length})</SelectItem>
          </SelectContent>
        </Select>
        {(statusFilter !== "all" || alertFilter !== "all" || docsFilter !== "all" || personalFilter !== "all" || typeFilter !== "all" || filter) && (
          <button onClick={() => { setStatusFilter("all"); setAlertFilter("all"); setDocsFilter("all"); setPersonalFilter("all"); setTypeFilter("all"); setFilter(""); }} className="text-xs text-zinc-500 hover:text-[var(--primary)] underline">Reset</button>
        )}
        <div className="flex items-center gap-1.5 flex-wrap w-full mt-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Tip:</span>
          {[{ code: "all", label: "Toate", emoji: "" }, ...VEHICLE_TYPES].map((t) => (
            <button
              key={t.code}
              onClick={() => setTypeFilter(t.code)}
              className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${typeFilter === t.code ? "bg-[var(--primary)] text-white border-[var(--primary)]" : `${t.cls || "bg-zinc-100 text-zinc-700 border-zinc-300"} hover:ring-1 hover:ring-zinc-400`}`}
              data-testid={`type-filter-${t.code}`}
            >
              {t.emoji} {t.label} ({t.code === "all" ? rows.filter((r) => !r.is_equipment && r.status !== "sold").length : rows.filter((r) => !r.is_equipment && r.status !== "sold" && carVehicleType(r) === t.code).length})
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-500 ml-auto">{filtered.length} / {rows.filter((r) => !r.is_equipment).length} autoturisme</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const s = notifStatus(c);
          const defect = isDefect(c);
          const openDefects = (c.defects || []).filter((d) => !d.fixed);
          const defectCount = openDefects.length;
          // Worst severity → defect bullet color (legacy single-defect cars: medium)
          const severities = openDefects.map((d) => d.severity || "medium");
          const worstSeverity = severities.includes("high")
            ? "high"
            : severities.includes("medium")
            ? "medium"
            : severities.includes("low")
            ? "low"
            : (defect ? "medium" : null);
          const defectBulletColor =
            worstSeverity === "high" ? "bg-red-600" :
            worstSeverity === "medium" ? "bg-orange-500" :
            worstSeverity === "low" ? "bg-yellow-400" : null;
          // Oil/revizie overdue: km since last revizie
          const km = Number(c.kilometri || 0);
          const lastKm = Number(c.revizie_last_km || 0);
          const gap = lastKm > 0 ? km - lastKm : 0;
          // Revizie scadentă după pragul explicit setat pe mașină (revizie_next_km)
          const nextKm = Number(c.revizie_next_km || 0);
          const revizieDue = nextKm > 0 && km >= nextKm;
          const overNext = revizieDue ? km - nextKm : 0;
          // Revizie urgentă (>15k) sau critică (>20k) sau prag depășit — banner extra-vizibil în card
          const reviziaUrgenta = gap > 20000 ? "critical" : (gap > 15000 || revizieDue) ? "urgent" : null;
          // Card border: amplificat pentru mecanic (magazioner) — toate alertele pulsează
          // Logica de severitate (de la cea mai gravă la cea mai puțin gravă):
          //   defect | revizie >20k | MFK expirat | severity=high → ROȘU pulsant
          //   revizie >15k | severity=medium | tires_worn → PORTOCALIU pulsant
          //   revizie >10k | severity=low | MFK expiră curând (≤30 zile) → AMBER pulsant (doar pt mecanic)
          const today = new Date().toISOString().slice(0, 10);
          const mfkExpired = c.mfk_expire && c.mfk_expire <= today;
          const mfkSoon = c.mfk_expire && !mfkExpired && (() => {
            const exp = new Date(c.mfk_expire);
            const diffDays = (exp - new Date(today)) / (1000 * 60 * 60 * 24);
            return diffDays <= 30;
          })();
          const tiresWorn = c.anvelope_status === "uzate" || c.anvelope_status === "uzata";
          const isRed = defect || reviziaUrgenta === "critical" || mfkExpired || worstSeverity === "high";
          const isOrange = !isRed && (reviziaUrgenta === "urgent" || worstSeverity === "medium" || tiresWorn);
          const isYellow = !isRed && !isOrange && (gap > 10000 || worstSeverity === "low" || mfkSoon);
          const borderCls = isRed
            ? "border-l-[8px] border-l-red-600 ring-2 ring-red-300 animate-pulse-red-soft"
            : isOrange
              ? "border-l-[8px] border-l-orange-500 ring-2 ring-orange-200 animate-pulse-orange-soft"
              : isMechanic && isYellow
                ? "border-l-[6px] border-l-amber-500 ring-1 ring-amber-200"
                : "border-l-4 border-l-[var(--green-valid)]";
          return (
            <div key={c.id} className="relative group">
              <Link to={`/autoturisme/${c.id}`} data-testid={`car-card-${c.id}`} className={`card-flat p-5 hover:border-[var(--primary)] transition-colors block ${borderCls}`}>
              {reviziaUrgenta && (
                <div
                  className={`mb-3 -mx-2 px-3 py-2 rounded-md flex items-center gap-2 border-2 ${reviziaUrgenta === "critical" ? "alert-banner-red border-red-500 text-red-900" : "alert-banner-orange border-orange-500 text-orange-900"}`}
                  data-testid={`car-revizie-banner-${c.id}`}
                  title={`Ultima revizie: ${lastKm.toLocaleString("ro-RO")} km · Acum: ${km.toLocaleString("ro-RO")} km${nextKm > 0 ? ` · Prag revizie: ${nextKm.toLocaleString("ro-RO")} km` : ""}`}
                >
                  <span className="text-xl leading-none">🛠</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-extrabold uppercase tracking-widest leading-tight">
                      {reviziaUrgenta === "critical" ? "REVIZIE CRITICĂ — STOP!" : revizieDue && gap <= 15000 ? "REVIZIE SCADENTĂ" : "REVIZIE URGENTĂ"}
                    </div>
                    <div className="text-xs font-bold mt-0.5">
                      {revizieDue ? (
                        <>+{Math.round(overNext).toLocaleString("ro-RO")} km <span className="font-normal opacity-80">peste pragul de revizie ({nextKm.toLocaleString("ro-RO")} km)</span></>
                      ) : (
                        <>+{Math.round(gap).toLocaleString("ro-RO")} km <span className="font-normal opacity-80">de la ultima revizie</span></>
                      )}
                    </div>
                  </div>
                  <span className="text-lg leading-none">⚠️</span>
                </div>
              )}
              <div className="flex items-start gap-4">
                <div className="relative">
                  {c.logo_file_id ? (
                    <AuthImg fileId={c.logo_file_id} className="w-16 h-16 rounded-md object-cover bg-zinc-50" />
                  ) : (
                    <div className="w-16 h-16 rounded-md bg-zinc-100 grid place-items-center text-zinc-400"><CarIcon size={24}/></div>
                  )}
                  {/* Bulina prioritate: defect=roșu pulsant; MFK expirat / revizie >20k=roșu non-pulsant; >15k=orange; >10k=galben */}
                  {defect && <span className="absolute -top-1 -right-1 bila-dot bila-red bila-pulse" title="Defect" />}
                  {!defect && s.red && <span className="absolute -top-1 -right-1 bila-dot bila-red" title="Atenție urgentă (MFK / revizie >20.000 km)" />}
                  {!defect && !s.red && s.orange && <span className="absolute -top-1 -right-1 bila-dot" style={{ background: "#F97316" }} title="Revizie >15.000 km" />}
                  {!defect && !s.red && !s.orange && s.yellow && <span className="absolute -top-1 -right-1 bila-dot" style={{ background: "#EAB308" }} title="Revizie >10.000 km" />}
                  {!defect && !s.red && !s.orange && !s.yellow && c.has_observations && <span className="absolute -top-1 -right-1 bila-dot bila-orange" title="Observație" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">{c.numar_inmatriculare || "—"}</span>
                    {(() => { const vt = VEHICLE_TYPES.find((t) => t.code === carVehicleType(c)); return vt ? (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${vt.cls}`} data-testid={`vt-badge-${c.id}`} title={c.nr_locuri ? `${vt.label} · ${c.nr_locuri} locuri` : vt.label}>
                        {vt.emoji} {vt.label.toUpperCase()}{c.nr_locuri ? ` · ${c.nr_locuri} loc` : ""}
                      </span>
                    ) : null; })()}
                    {c.intern_number && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--primary-subtle)] text-[var(--primary)] font-bold">#{c.intern_number}</span>}
                    {c.tool_set_color && (() => {
                      const COLOR_META = {
                        rosu: { emoji: "🔴", label: "Roșu", bg: "bg-red-500" },
                        galben: { emoji: "🟡", label: "Galben", bg: "bg-yellow-500" },
                        albastru: { emoji: "🔵", label: "Albastru", bg: "bg-blue-500" },
                        verde: { emoji: "🟢", label: "Verde", bg: "bg-emerald-500" },
                        gri: { emoji: "⚪", label: "Gri", bg: "bg-zinc-500" },
                        mov: { emoji: "🟣", label: "Mov", bg: "bg-purple-500" },
                      };
                      const meta = COLOR_META[c.tool_set_color];
                      if (!meta) return null;
                      return (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.bg} text-white font-bold inline-flex items-center gap-1`}
                          title={`Set scule: ${meta.label}`}
                          data-testid={`car-card-tool-set-${c.id}`}
                        >
                          {meta.emoji} {meta.label}
                        </span>
                      );
                    })()}
                    {/* Defect bullets - one per open defect, colored by worst severity */}
                    {defectCount > 0 && (
                      <span className="inline-flex items-center gap-0.5" title={`${defectCount} defect${defectCount === 1 ? "" : "e"} raportat${defectCount === 1 ? "" : "e"}`}>
                        {Array.from({ length: Math.min(defectCount, 5) }).map((_, i) => (
                          <span key={i} className={`w-2 h-2 rounded-full ${defectBulletColor || "bg-zinc-400"}`}/>
                        ))}
                        {defectCount > 5 && <span className="text-[9px] text-zinc-500 ml-0.5">+{defectCount - 5}</span>}
                      </span>
                    )}
                    {/* Oil icon - revizie overdue indicator */}
                    <ServiceLamp gap={gap}/>
                    {/* Tires worn indicator */}
                    {c.tires_worn && (
                      <span title="Cauciucuri uzate" className="text-base leading-none text-red-600">🛞</span>
                    )}
                    {defect && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--red-bila-bg)] text-[var(--red-bila)] font-bold">DEFECT</span>}
                    {(() => {
                      const pc = partsCounts[c.id];
                      if (!pc || pc.count <= 0) return null;
                      const hasLow = pc.low_stock > 0;
                      return (
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/inventar?tab=car-parts&car=${c.id}`); }}
                          className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-0.5 transition-colors border ${hasLow ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200" : "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200"}`}
                          title={`${pc.count} ${pc.count === 1 ? "piesă" : "piese"} în stoc · Σ ${Math.round(pc.total_qty)} buc${hasLow ? ` · ${pc.low_stock} cu stoc scăzut` : ""} · click pentru a vedea`}
                          data-testid={`car-parts-badge-${c.id}`}
                        >
                          🔧 {pc.count}{hasLow ? <AlertTriangle size={9} className="text-amber-700"/> : null}
                        </button>
                      );
                    })()}
                    {c.is_personal && (() => {
                      const owner = c.personal_owner_person_id ? persons.find((p) => p.id === c.personal_owner_person_id) : null;
                      const ownerLabel = owner ? `${owner.nume} ${owner.prenume}`.trim() : null;
                      return (
                        <span
                          className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-bold border border-violet-200 inline-flex items-center gap-0.5"
                          title={ownerLabel ? `Mașină personală · ${ownerLabel}` : "Mașină personală — nu apare în Planificator"}
                          data-testid={`car-personal-badge-${c.id}`}
                        >
                          🚫 PERSONAL{ownerLabel ? ` · ${ownerLabel}` : ""}
                        </span>
                      );
                    })()}
                    {/* Permise de parcare per oraș (cu indicator validitate) */}
                    {(c.parking_permits || []).length > 0 && (() => {
                      const today = new Date().toISOString().slice(0, 10);
                      return (c.parking_permits || []).map((p) => {
                        const expired = p.valid_until && p.valid_until < today;
                        const cls = expired
                          ? "bg-red-100 text-red-700 border-red-200"
                          : "bg-blue-50 text-blue-800 border-blue-200";
                        const tip = `${p.city_name || p.city_code}${p.zone ? ` · ${p.zone}` : ""}${p.valid_until ? ` · până la ${p.valid_until.split("-").reverse().join(".")}` : ""}${expired ? " (EXPIRAT)" : ""}`;
                        return (
                          <span
                            key={p.id}
                            className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold border ${cls}`}
                            title={tip}
                            data-testid={`car-permit-${c.id}-${p.city_code}`}
                          >
                            🅿️ {p.city_code}
                          </span>
                        );
                      });
                    })()}
                  </div>
                  <div className="font-display font-bold text-lg truncate">{c.marca || "—"} {c.model || ""}</div>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{Math.round(c.kilometri || 0)} km · {c.an_fabricatie || "—"}</span>
                    {c.culoare && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-medium text-zinc-500 px-1.5 py-0.5 rounded bg-zinc-50 border border-zinc-200"
                        title={`Culoare: ${c.culoare}`}
                        data-testid={`car-color-${c.id}`}
                      >
                        <span className="w-3 h-3 rounded-full border border-zinc-300" style={{ background: cssColorFor(c.culoare) }}/>
                        {c.culoare}
                      </span>
                    )}
                    {(c.documente?.length > 0) ? (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[var(--green-bg)] text-[var(--green-valid)] font-bold text-[10px]" data-testid={`docs-count-${c.id}`} title={`${c.documente.length} document${c.documente.length === 1 ? "" : "e"} atașat${c.documente.length === 1 ? "" : "e"}`}>
                        📎 {c.documente.length}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-400 text-[10px]" title="Niciun document atașat">
                        📎 0
                      </span>
                    )}
                  </div>
                  {(c.current_drivers?.length > 0 || c.top_driver) && (
                    <div className="mt-2 space-y-1 text-[11px]">
                      {c.current_drivers?.length > 0 && (
                        <div className="flex items-center gap-1.5 text-emerald-700" data-testid={`car-current-driver-${c.id}`}>
                          <span className="text-[9px] uppercase font-bold tracking-wider">🟢 Azi</span>
                          <span className="truncate">{c.current_drivers.map((d) => d.name).join(", ")}</span>
                        </div>
                      )}
                      {c.top_driver && (!c.current_drivers?.length || !c.current_drivers.some((d) => d.id === c.top_driver.id)) && (
                        <div className="flex items-center gap-1.5 text-zinc-500" data-testid={`car-top-driver-${c.id}`} title={`${c.top_driver.hours} ore conduse total`}>
                          <span className="text-[9px] uppercase font-bold tracking-wider">⭐ Uzual</span>
                          <span className="truncate">{c.top_driver.name}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {(() => {
                const missing = missingCarFields(c);
                const missingDocs = missingCarDocs(c);
                const total = missing.length + missingDocs.length;
                if (total === 0) return null;
                const labels = [
                  ...missing.map((m) => m.label),
                  ...missingDocs.map((m) => `📎 ${m.label}`),
                ];
                return (
                  <div
                    className="mt-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-2"
                    data-testid={`car-missing-banner-${c.id}`}
                    title={`Lipsesc: ${labels.join(", ")}`}
                  >
                    <AlertCircle size={14} className="shrink-0 mt-0.5 text-amber-600"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold uppercase tracking-wider">
                        {missingDocs.length > 0 && missing.length === 0
                          ? `Documente lipsă · încarcă ${missingDocs.length === 1 ? "ce lipsește" : `cele ${missingDocs.length} documente lipsă`}`
                          : `Date lipsă · introdu ${total === 1 ? "ce lipsește" : `cele ${total} elemente lipsă`}`}
                      </div>
                      <div className="text-[11px] mt-0.5 truncate">
                        {labels.join(", ")}
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-amber-700 shrink-0">Completează →</span>
                  </div>
                );
              })()}
            </Link>
              {canQuickAssign && c.status !== "sold" && (
                <button
                  type="button"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickDriverCar(c); }}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-full bg-white shadow-sm border border-zinc-200 text-blue-600 hover:border-blue-400 hover:scale-110 z-10"
                  title="Schimbă șofer azi"
                  data-testid={`quick-driver-${c.id}`}
                >
                  🚗
                </button>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div className="text-sm text-zinc-500 col-span-full">Niciun autoturism găsit.</div>}
      </div>

      {equipmentItems.length > 0 && (
        <div className="mt-12" data-testid="equipment-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-display text-xl font-bold text-zinc-700">🛠 Echipamente</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">{equipmentItems.length}</span>
            <span className="text-xs text-zinc-400">doar evidență combustibil</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {equipmentItems.map((c) => (
              <Link key={c.id} to={`/autoturisme/${c.id}`} data-testid={`equipment-card-${c.id}`} className="card-flat p-4 hover:border-amber-400 transition-colors block border-l-4 border-l-amber-500">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-md bg-amber-50 grid place-items-center text-2xl">⛽</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm truncate">{c.model || c.marca || "Echipament"}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Istoric alimentări combustibil →</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {soldCars.length > 0 && (
        <div className="mt-12" data-testid="sold-cars-section">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-display text-xl font-bold text-zinc-700">🚫 Vândute / Indisponibile</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-bold">{soldCars.length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
            {soldCars.map((c) => (
              <Link key={c.id} to={`/autoturisme/${c.id}`} data-testid={`sold-car-card-${c.id}`} className="card-flat p-4 hover:border-zinc-400 transition-colors block border-l-4 border-l-zinc-400 grayscale hover:grayscale-0">
                <div className="flex items-start gap-3">
                  {c.logo_file_id ? (
                    <AuthImg fileId={c.logo_file_id} className="w-12 h-12 rounded-md object-cover bg-zinc-50"/>
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-zinc-100 grid place-items-center text-zinc-400"><CarIcon size={20}/></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono text-xs text-zinc-500">{c.numar_inmatriculare || "—"}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-200 text-zinc-700 font-bold">VÂNDUTĂ</span>
                    </div>
                    <div className="font-display font-bold text-sm truncate">{c.marca || "—"} {c.model || ""}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {c.sold_at && <span>📅 {c.sold_at}</span>}
                      {c.sale_price ? <span className="ml-2 font-semibold text-emerald-600">💰 {Number(c.sale_price).toLocaleString("ro-RO")} CHF</span> : null}
                    </div>
                    {c.sold_to && <div className="text-[11px] text-zinc-400 mt-0.5">Cumpărător: {c.sold_to}</div>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {quickDriverCar && (
        <QuickDriverDialog
          car={quickDriverCar}
          persons={persons}
          onClose={() => setQuickDriverCar(null)}
          onSaved={() => { setQuickDriverCar(null); load(); }}
        />
      )}
    </div>
  );
}

function QuickDriverDialog({ car, persons, onClose, onSaved }) {
  const initial = (car.current_drivers || []).map((d) => d.id);
  const [selected, setSelected] = useState(initial);
  const [search, setSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggle = (pid) => setSelected((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);

  const filtered = persons.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return `${p.nume || ""} ${p.prenume || ""}`.toLowerCase().includes(q);
  });

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/cars/${car.id}/quick-driver`, { person_ids: selected });
      toast.success(selected.length === 0 ? "Șoferi eliminați pentru azi" : "Șofer atribuit");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="quick-driver-dialog">
        <DialogHeader>
          <DialogTitle>🚗 Schimbă șofer azi · {car.numar_inmatriculare || ""} {car.marca || ""} {car.model || ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Caută…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus data-testid="quick-driver-search"/>
          <div className="max-h-72 overflow-y-auto border rounded">
            {filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-zinc-400">Nicio persoană activă.</div>
            ) : filtered.map((p) => {
              const sel = selected.includes(p.id);
              return (
                <button key={p.id} type="button" onClick={() => toggle(p.id)}
                  className={`w-full text-left px-3 py-2 text-sm border-b last:border-0 flex items-center gap-2 ${sel ? "bg-blue-50 font-semibold" : "hover:bg-zinc-50"}`}
                  data-testid={`quick-driver-pick-${p.id}`}>
                  <span className={`w-4 h-4 rounded border ${sel ? "bg-blue-600 border-blue-600 text-white" : "border-zinc-300"} grid place-items-center text-[10px]`}>
                    {sel ? "✓" : ""}
                  </span>
                  {p.color && <span className="w-2 h-2 rounded-full" style={{ background: p.color }}/>}
                  <span>{p.nume} {p.prenume}</span>
                </button>
              );
            })}
          </div>
          <div className="text-xs text-zinc-500">
            {selected.length === 0
              ? "Niciun șofer selectat — vei elimina atribuirea pentru azi."
              : `${selected.length} șofer${selected.length === 1 ? "" : "i"} selectat${selected.length === 1 ? "" : "i"}.`}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-driver-save">
            {submitting ? "Se salvează…" : "Salvează"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ====== Stoc consumabile mașini — banner agregat (ulei + filtre + alte fluide) ======
// Praguri de alarmă (minimum în stoc — dacă valoarea scade sub, banner-ul devine roșu).
// Discutate cu utilizatorul: ulei motor minim 40 L. Pentru filtre nu există încă
// un prag — folosim 4 buc default (ajustabil din UI ulterior dacă e nevoie).
const CONSUMABLE_THRESHOLDS = {
  ulei_motor: 40,
  antigel: 10,
  lichid_spalator: 10,
  lichid_frana: 5,
  filtru_ulei: 4,
  filtru_aer: 4,
  filtru_combustibil: 4,
  filtru_habitaclu: 4,
};

const CONSUMABLE_META = {
  ulei_motor: { icon: "🛢️", label: "Ulei motor", color: "bg-amber-100 text-amber-900 border-amber-300" },
  antigel: { icon: "❄️", label: "Antigel", color: "bg-cyan-100 text-cyan-900 border-cyan-300" },
  lichid_spalator: { icon: "💧", label: "Lichid spălător", color: "bg-blue-100 text-blue-900 border-blue-300" },
  lichid_frana: { icon: "🛑", label: "Lichid frână", color: "bg-red-100 text-red-900 border-red-300" },
  filtru_ulei: { icon: "🛢️", label: "Filtru ulei", color: "bg-amber-50 text-amber-900 border-amber-200" },
  filtru_aer: { icon: "💨", label: "Filtru aer", color: "bg-sky-50 text-sky-900 border-sky-200" },
  filtru_combustibil: { icon: "⛽", label: "Filtru combustibil", color: "bg-orange-50 text-orange-900 border-orange-200" },
  filtru_habitaclu: { icon: "🧊", label: "Filtru habitaclu", color: "bg-violet-50 text-violet-900 border-violet-200" },
};

export function CarConsumablesStockBanner() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/inventory/car-consumables-stock").then((r) => setData(r.data)).catch(() => setData({ totals: {} }));
  }, []);
  if (!data || Object.keys(data.totals || {}).length === 0) return null;
  const entries = Object.entries(data.totals).sort((a, b) => b[1] - a[1]);
  const lowEntries = entries.filter(([t, qty]) => qty < (CONSUMABLE_THRESHOLDS[t] ?? Infinity));
  const hasLow = lowEntries.length > 0;
  return (
    <div className={`card-flat p-3 mb-4 ${hasLow ? "border-l-4 border-red-500 bg-red-50" : ""}`} data-testid="car-consumables-stock-banner">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">🚗 Stoc consumabile mașini</div>
        {hasLow && (
          <div className="text-[11px] font-bold text-red-700 inline-flex items-center gap-1" data-testid="stock-low-warning">
            ⚠️ Stoc scăzut — comandă urgent ({lowEntries.length})
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([type, qty]) => {
          const meta = CONSUMABLE_META[type] || { icon: "📦", label: type, color: "bg-zinc-100 text-zinc-900 border-zinc-300" };
          const unit = (data.units || {})[type] || "buc";
          const threshold = CONSUMABLE_THRESHOLDS[type];
          const isLow = threshold != null && qty < threshold;
          return (
            <Link
              key={type}
              to={`/inventar?q=${encodeURIComponent(meta.label.toLowerCase())}`}
              className={`px-2.5 py-1 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 hover:brightness-95 ${isLow ? "bg-red-100 text-red-900 border-red-400 animate-pulse" : meta.color}`}
              data-testid={`consumable-stock-${type}`}
              title={threshold ? `Prag alarmă: ${threshold} ${unit}` : ""}
            >
              <span>{meta.icon}</span>
              <span>{meta.label}:</span>
              <span className="tabular-nums">{qty} {unit}</span>
              {isLow && <span className="ml-0.5">⚠️</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

